/**
 * The activity ledger (credits wave 2 F8 §3.3) of the paying wallet: one line per video with the total it
 * was charged (Rahul, 15 Oct: never split), then credits added and the other movements, each with the
 * balance after. Credits only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, History, Loader2 } from 'lucide-react';
import { billingAPI, type CreditActivityByVideo, type CreditActivityEntry, type CreditActivityVideoGroup } from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

export const ACTIVITY_PAGE_SIZE = 50;
export const ACTIVITY_ANCHOR = 'activity';
export const CSV_MAX_ROWS = 5000;
const CSV_PAGE_SIZE = 200;
const DAY_MS = 24 * 60 * 60 * 1000;

type Range = '30' | '90' | 'all';
type Kind = 'all' | 'videos' | 'added';

const RANGES: Array<{ value: Range; label: string }> = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];
const KINDS: Array<{ value: Kind; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'videos', label: 'Videos' },
  { value: 'added', label: 'Credits added' },
];

function windowOf(range: Range): { from?: string; to?: string } {
  if (range === 'all') return {};
  const to = new Date();
  return { from: new Date(to.getTime() - Number(range) * DAY_MS).toISOString(), to: to.toISOString() };
}

const isAdded = (e: CreditActivityEntry) => e.credits > 0;

const csvCell = (v: string | number | null) => {
  const s = v === null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function activityCsv(videos: CreditActivityVideoGroup[], entries: CreditActivityEntry[]): string {
  const rows = [
    ...videos.map((g) => ({ at: g.lastAt ?? '', cells: [g.lastAt ?? '', 'Video', g.video.title, 0 - g.charged, null] })),
    ...entries.map((e) => ({ at: e.at, cells: [e.at, e.label, e.video?.title ?? '', e.credits, e.balanceAfter] })),
  ]
    .sort((a, b) => b.at.localeCompare(a.at))
    .slice(0, CSV_MAX_ROWS);
  return ['date,what,video,credits,balance after', ...rows.map((r) => r.cells.map(csvCell).join(','))].join('\n') + '\n';
}

async function allEntries(span: { from?: string; to?: string }): Promise<CreditActivityEntry[]> {
  const out: CreditActivityEntry[] = [];
  let cursor: string | null = null;
  do {
    const page = await billingAPI.getActivity({ limit: CSV_PAGE_SIZE, ...span, ...(cursor ? { cursor } : {}) });
    out.push(...page.entries);
    cursor = page.nextCursor;
  } while (cursor && out.length < CSV_MAX_ROWS);
  return out;
}

function download(name: string, csv: string) {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

const NUMBER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

export function formatCreditAmount(n: number): string {
  const abs = Math.abs(n);
  return `${n < 0 ? '−' : ''}${abs > 0 && abs < 0.005 ? '<0.01' : NUMBER.format(abs)}`;
}

export function formatCredits(n: number, opts: { signed?: boolean } = {}): string {
  const sign = opts.signed && n > 0 ? '+' : '';
  return `${sign}${formatCreditAmount(n)} credits`;
}

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const TIER_LABEL: Record<string, string> = { basic: 'Basic', pro: 'Pro', ultramax: 'Ultramax' };

export function CreditActivity() {
  const [range, setRange] = useState<Range>('30');
  const [kind, setKind] = useState<Kind>('all');
  const [data, setData] = useState<CreditActivityByVideo | null>(null);
  const [entries, setEntries] = useState<CreditActivityEntry[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [failed, setFailed] = useState(false);

  const span = useMemo(() => windowOf(range), [range]);

  const loadEntries = useCallback(async (after: string | null) => {
    setLoading(true);
    try {
      const page = await billingAPI.getActivity({ limit: ACTIVITY_PAGE_SIZE, ...span, ...(after ? { cursor: after } : {}) });
      const rows = page.entries;
      setEntries((prev) => (after && prev ? [...prev, ...rows] : rows));
      setCursor(page.nextCursor);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [span]);

  useEffect(() => {
    setFailed(false);
    billingAPI.getActivityByVideo(span).then(setData).catch(() => setFailed(true));
    void loadEntries(null);
  }, [span, loadEntries]);

  useEffect(() => {
    if (data && window.location.hash === `#${ACTIVITY_ANCHOR}`) {
      document.getElementById(ACTIVITY_ANCHOR)?.scrollIntoView({ block: 'start' });
    }
  }, [data]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = kind === 'videos' ? [] : await allEntries(span);
      const videoRows = kind === 'added' ? [] : data?.videos ?? [];
      download('4k-labs-credit-activity.csv', activityCsv(videoRows, kind === 'added' ? rows.filter(isAdded) : rows));
    } catch {
      setFailed(true);
    } finally {
      setExporting(false);
    }
  };

  const shownVideos = kind === 'added' ? [] : data?.videos ?? [];
  const shownEntries = kind === 'videos' ? [] : kind === 'added' ? entries?.filter(isAdded) ?? [] : entries ?? [];
  const empty = data && entries && shownVideos.length === 0 && shownEntries.length === 0;

  return (
    <Card id={ACTIVITY_ANCHOR}>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg">
            <History className="h-4 w-4" /> Activity
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">What each video was charged, and the credits added.</p>
        </div>
        {data && (
          <div className="text-right shrink-0">
            <div className="text-xs text-muted-foreground">Balance</div>
            <div className="font-medium tabular-nums">{formatCredits(data.balance.credits)}</div>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2 pb-0">
        <FilterSelect label="Date range" value={range} options={RANGES} onChange={setRange} />
        <FilterSelect label="Type" value={kind} options={KINDS} onChange={setKind} />
        <Button variant="outline" size="sm" className="ml-auto h-8" onClick={() => void exportCsv()} disabled={exporting || !data}>
          {exporting ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-1.5 h-3.5 w-3.5" />}
          Export CSV
        </Button>
      </CardContent>
      <CardContent className="space-y-6">
        {(!data || !entries) && !failed && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        {failed && <p className="text-sm text-destructive">Could not load activity</p>}
        {empty && <p className="text-sm text-muted-foreground">No activity yet.</p>}
        {shownVideos.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-medium">By video</h3>
            <ul className="divide-y rounded-md border">
              {shownVideos.map((g) => <VideoRow key={g.video.id} group={g} />)}
            </ul>
          </section>
        )}
        {shownEntries.length > 0 && (
          <section>
            <h3 className="mb-2 text-sm font-medium">{kind === 'added' ? 'Credits added' : 'Credits added and other activity'}</h3>
            <ul className="divide-y rounded-md border px-4">
              {shownEntries.map((e) => <ActivityEntryRow key={e.id} entry={e} />)}
            </ul>
            {loading && <Loader2 className="my-2 h-4 w-4 animate-spin text-muted-foreground" />}
            {cursor && !loading && (
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => void loadEntries(cursor)}>
                Load more
              </Button>
            )}
          </section>
        )}
      </CardContent>
    </Card>
  );
}

function FilterSelect<T extends string>({ label, value, options, onChange }: {
  label: string; value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void;
}) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      className="h-8 rounded-md border border-input bg-background px-2 text-sm"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function VideoRow({ group }: { group: CreditActivityVideoGroup }) {
  const tier = group.qualityTier ? TIER_LABEL[group.qualityTier] ?? group.qualityTier : null;
  return (
    <li data-testid="activity-video" className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
      <div className="min-w-0">
        <div className="truncate font-medium">{group.video.title}</div>
        <div className="text-xs text-muted-foreground">
          {[tier, group.lastAt && `Last charge ${formatTime(group.lastAt)}`].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className="shrink-0 font-medium tabular-nums">{formatCredits(0 - group.charged, { signed: true })}</div>
    </li>
  );
}

function ActivityEntryRow({ entry }: { entry: CreditActivityEntry }) {
  return (
    <li data-testid="activity-entry" className="flex items-center justify-between gap-4 py-2 text-sm">
      <div className="min-w-0">
        <div className="truncate">{entry.label}</div>
        <div className="text-xs text-muted-foreground">{formatTime(entry.at)}</div>
      </div>
      <div className="shrink-0 text-right">
        <div className={`font-medium tabular-nums ${entry.credits < 0 ? '' : 'text-primary'}`}>{formatCredits(entry.credits, { signed: true })}</div>
        <div className="text-xs text-muted-foreground tabular-nums">
          {entry.balanceAfter === null ? 'Balance after —' : `Balance after ${formatCreditAmount(entry.balanceAfter)}`}
        </div>
      </div>
    </li>
  );
}
